import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ReadonlySectionHeader} from '@github-ui/issue-metadata/ReadonlySectionHeader'
import {Section} from '@github-ui/issue-metadata/Section'
import {SectionHeader} from '@github-ui/issue-metadata/SectionHeader'
import type {IssuePickerItem} from '@github-ui/item-picker/IssuePicker'
import {RepositoryAndIssuePicker} from '@github-ui/item-picker/RepositoryAndIssuePicker'
import {
  type CodeScanningAlert,
  CodeScanningAlertPicker,
  type CodeScanningAlertStatus,
} from '@github-ui/item-picker-alert/CodeScanningAlertPicker'
import {commitRemoveSubIssueMutation} from '@github-ui/sub-issues/commitRemoveSubIssueMutation'
import {useAlert} from '@github-ui/sub-issues/useAlert'
import {useCanEditSubIssues} from '@github-ui/sub-issues/useCanEditSubIssues'
import {CommandActionListItem, GlobalCommands} from '@github-ui/ui-commands'
import {LockIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Banner, CounterLabel} from '@primer/react'
import type React from 'react'
import {type HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, type PreloadedQuery, useFragment, usePreloadedQuery, useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../../constants/labels'
import {useAlertTrackingErrors} from '../../../hooks/use-alert-tracking-errors'
import {useDependencyAlerts} from '../../../hooks/use-dependencies-alert'
import {setParentMutation} from '../../../mutations/set-parent-mutation'
import {RelationshipsAlertDialog} from '../../shared/RelationshipsAlertDialog'
import type {RelationshipsSectionFragment$key} from './__generated__/RelationshipsSectionFragment.graphql'
import type {RelationshipsSectionQuery} from './__generated__/RelationshipsSectionQuery.graphql'
import {DependenciesPicker, type DependenciesPickerProps} from './DependenciesPicker'
import {DependencyIssue} from './DependencyIssue'
import {LazyRelationshipsBlockedByListView} from './LazyRelationshipsBlockedByListView'
import {LazyRelationshipsBlockingListView} from './LazyRelationshipsBlockingListView'
import {LazyRelationshipsSecurityAlertsListView} from './LazyRelationshipsSecurityAlertsListView'
import {ParentIssue} from './ParentIssue'
import styles from './RelationshipsSection.module.css'
import {RelationshipsViewAllButton} from './RelationshipsViewAllButton'
import {SecurityAlert} from './SecurityAlert'

const VISIBLE_ISSUES_THRESHOLD = 3
const VISIBLE_ALERTS_THRESHOLD = 3
type DependenciesPickerState = DependenciesPickerProps['type'] | null

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const RelationshipsSectionGraphqlQuery = graphql`
  query RelationshipsSectionQuery($owner: String!, $repo: String!, $number: Int!) {
    repository(owner: $owner, name: $repo) {
      issue(number: $number) {
        ...RelationshipsSectionFragment
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const RelationshipsSectionFragment = graphql`
  fragment RelationshipsSectionFragment on Issue {
    id
    databaseId
    repository {
      nameWithOwner
      owner {
        login
      }
    }
    parent {
      id
      ...ParentIssueFragment
    }
    topBlockedBy: blockedBy(first: 3, ranked: true) {
      nodes {
        id
        ...DependencyIssueFragment
      }
      pageInfo {
        hasNextPage
      }
    }
    topBlocking: blocking(first: 3, ranked: true) {
      nodes {
        id
        ...DependencyIssueFragment
      }
      pageInfo {
        hasNextPage
      }
    }
    topSecurityAlerts: securityAlerts(first: 50) {
      totalCount
      nodes {
        number
        repository {
          nameWithOwner
        }
        status
        title
      }
      pageInfo {
        hasNextPage
      }
    }
    issueDependenciesSummary {
      blockedBy
      blocking
    }
    ...useCanEditSubIssues
  }
`

type RelationshipsSectionProps = {
  queryRef: PreloadedQuery<RelationshipsSectionQuery>
  onLinkClick?: (event: MouseEvent) => void
  onSelection: () => void
  insideSidePanel?: boolean
  readonly: boolean
}

type RelationshipsSectionInternalProps = Omit<RelationshipsSectionProps, 'queryRef'> & {
  issue: RelationshipsSectionFragment$key
  insideSidePanel?: boolean
}

function checkForInaccessibleIssues(totalIssuesCount: number, visibleIssuesCount: number, isLastPage: boolean) {
  // if we have more total issues than what's visible then we have hidden issues.
  const hasHiddenIssues = totalIssuesCount > visibleIssuesCount

  // if fewer than 3 issues are visible, it means there are hidden/private issues.
  // if exactly 3 issues are visible (which is the maximum per page), and there's no next page of results, it also indicates that some issues are private.
  const issuesAreHiddenDueToPrivacy =
    visibleIssuesCount < VISIBLE_ISSUES_THRESHOLD || (visibleIssuesCount === VISIBLE_ISSUES_THRESHOLD && isLastPage)

  return hasHiddenIssues && issuesAreHiddenDueToPrivacy
}

export function RelationshipsSectionFallback() {
  return (
    <Section
      sectionHeader={<ReadonlySectionHeader title={LABELS.sectionTitles.relationships} />}
      emptyText={LABELS.emptySections.relationships}
    />
  )
}

export function RelationshipsSection({queryRef, ...rest}: RelationshipsSectionProps) {
  const preloadedData = usePreloadedQuery<RelationshipsSectionQuery>(RelationshipsSectionGraphqlQuery, queryRef)
  return preloadedData.repository && preloadedData.repository.issue ? (
    <RelationshipsSectionInternal issue={preloadedData.repository.issue} {...rest} />
  ) : null
}

export function RelationshipsSectionInternal({
  issue,
  onLinkClick,
  insideSidePanel,
  readonly,
  onSelection,
}: RelationshipsSectionInternalProps) {
  const fragmentIssue = useFragment(RelationshipsSectionFragment, issue)
  const {parent, topBlockedBy, topBlocking, topSecurityAlerts, repository, id, databaseId, issueDependenciesSummary} =
    fragmentIssue

  const environment = useRelayEnvironment()
  const {alert, resetAlert, showServerAlert} = useAlert()
  const {
    alerts: dependencyAlerts,
    resetAlerts: resetDependencyAlerts,
    setAlertsFromErrors: setDependencyAlertsFromErrors,
  } = useDependencyAlerts()
  const {
    errors: alertTrackingErrors,
    resetErrors: resetAlertTrackingErrors,
    setErrors: setAlertTrackingErrors,
  } = useAlertTrackingErrors()
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const [pickerType, setPickerType] = useState<'Issue' | 'Repository' | null>(null)
  const [dependenciesPickerState, setDependenciesPickerState] = useState<DependenciesPickerState>(null)
  const [alertPickerOpen, setAlertPickerOpen] = useState(false)
  const canEditSubIssues = useCanEditSubIssues(fragmentIssue)

  useEffect(() => {
    if (menuOpen) {
      setPickerType(null)
      setDependenciesPickerState(null)
      setAlertPickerOpen(false)
    }
  }, [menuOpen])

  const onDependenciesPickerClose = useCallback(() => {
    setDependenciesPickerState(null)
  }, [])

  /**
   * When the issue-viewer is rendered within the side-panel, two issue-viewers are rendered at a time. This results
   * in `<GlobalCommands>` activating both pickers at once. To remedy this, we check if the side-panel is open and
   * whether or not the current issue-viewer is within the side-panel.
   *
   * If insideSidePanel is false, the issue-viewer is not inside the open side-panel.
   * If insideSidePanel is undefined, the side panel is not open.
   */
  const shouldAcceptCommand = insideSidePanel || insideSidePanel === undefined

  const onIssueSelection = useCallback(
    (selectedIssue: IssuePickerItem[]) => {
      if (!selectedIssue[0]) {
        // No selected issue means that an issue was deselected, so we remove the parent if there is one
        if (parent) {
          commitRemoveSubIssueMutation({
            environment,
            input: {
              issueId: parent.id,
              subIssueId: id,
            },
            onCompleted: onSelection,
          })
        }
      } else {
        setParentMutation({
          environment,
          input: {
            subIssueId: id,
            issueId: selectedIssue[0].id,
            replaceParent: true,
          },
          onCompleted: onSelection,
          onError: error => {
            showServerAlert(error)
          },
        })
      }
    },
    [environment, id, parent, showServerAlert, onSelection],
  )

  const onPickerTypeChange = useCallback((t: 'Issue' | 'Repository' | null) => setPickerType(t), [setPickerType])

  const hasBlockedBy = topBlockedBy?.nodes && topBlockedBy.nodes.length > 0
  const hasBlocking = topBlocking?.nodes && topBlocking.nodes.length > 0
  const hasSecurityAlerts =
    isFeatureEnabled('code_scanning_alert_tracking_links_phase_2') &&
    topSecurityAlerts?.nodes &&
    topSecurityAlerts.nodes.length > 0

  const hasViewAllBlockedBy = topBlockedBy?.pageInfo?.hasNextPage
  const hasViewAllBlocking = topBlocking?.pageInfo?.hasNextPage
  const hasViewAllSecurityAlerts = (topSecurityAlerts?.nodes?.length ?? 0) > VISIBLE_ALERTS_THRESHOLD

  const hasInaccessibleBlockedByIssues = useMemo(() => {
    const totalBlockedByCount = issueDependenciesSummary.blockedBy || 0
    const visibleIssuesCount = topBlockedBy?.nodes?.length || 0
    const isLastPage = !topBlockedBy?.pageInfo?.hasNextPage

    return checkForInaccessibleIssues(totalBlockedByCount, visibleIssuesCount, isLastPage)
  }, [issueDependenciesSummary, topBlockedBy])

  const hasInaccessibleBlockingIssues = useMemo(() => {
    const totalBlockingCount = issueDependenciesSummary.blocking || 0
    const visibleIssuesCount = topBlocking?.nodes?.length || 0
    const isLastPage = !topBlocking?.pageInfo?.hasNextPage

    return checkForInaccessibleIssues(totalBlockingCount, visibleIssuesCount, isLastPage)
  }, [issueDependenciesSummary, topBlocking])

  const hasDependencies =
    hasBlockedBy || hasBlocking || hasSecurityAlerts || hasInaccessibleBlockedByIssues || hasInaccessibleBlockingIssues
  const showDividerForSecurityAlerts =
    parent || hasBlockedBy || hasInaccessibleBlockedByIssues || hasBlocking || hasInaccessibleBlockingIssues

  const hasContent = useMemo(() => {
    return parent || hasDependencies
  }, [parent, hasDependencies])

  useEffect(() => {
    if (hasDependencies) {
      sendEvent('issue_viewer.dependencies.shown')
    }
    // we only want to send the event once on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // As currently designed, the ItemPicker requires an element to render as the anchor. This gets eventually
  // passed down to the SelectPanel which will handle rendering this element. In reality though, we don't want
  // this component to be responsible for rendering an element, we just want it to attach to the ref to an
  // existing anchor, so we do that here and return an empty element to "render"
  const sectionHeaderRef = useRef<HTMLButtonElement | null>(null)
  const itemPickerAnchorElement = (props: HTMLAttributes<HTMLElement>) => {
    const {ref} = props as {
      ref: React.RefObject<HTMLButtonElement | null>
    }
    if (ref) {
      ref.current = sectionHeaderRef.current
    }
    // We don't actually want to render any element, as we are relying on the rendered section header
    return <></>
  }

  const initialAlertSelection: CodeScanningAlert[] = useMemo(() => {
    return (
      topSecurityAlerts?.nodes?.map(a => ({
        number: a?.number || 0,
        repository: {
          nameWithOwner: a?.repository?.nameWithOwner || '',
        },
        state: a?.status as CodeScanningAlertStatus,
        title: a?.title || '',
      })) ?? []
    )
  }, [topSecurityAlerts])

  return (
    <Section
      id="sidebar-relationships-section"
      emptyText={hasContent ? undefined : LABELS.emptySections.relationships}
      sectionHeader={
        <>
          {shouldAcceptCommand && (
            <GlobalCommands
              commands={{
                'issue-viewer:edit-parent': () => {
                  // Close relationship type menu in case it's open
                  setMenuOpen(false)
                  setPickerType('Issue')
                },
                'issue-viewer:mark-blocked-by': () => {
                  setMenuOpen(false)
                  setDependenciesPickerState('blockedBy')
                },
                'issue-viewer:mark-blocking': () => {
                  setMenuOpen(false)
                  setDependenciesPickerState('blocking')
                },
                'issue-viewer:edit-security-alert': () => {
                  setMenuOpen(false)
                  setAlertPickerOpen(true)
                },
              }}
            />
          )}
          {/* We render the section header separate of both the picker and the action menu that will anchor to it.
              we set up a ref for those other elements to use to anchor to instead */}
          <SectionHeader
            ref={sectionHeaderRef}
            buttonProps={{
              onClick: () => setMenuOpen(o => !o),
            }}
            readonly={!canEditSubIssues || readonly}
            title={LABELS.sectionTitles.relationships}
          />
          <RepositoryAndIssuePicker
            onPickerTypeChange={onPickerTypeChange}
            selectedIssueIds={parent ? [parent.id] : []}
            hiddenIssueIds={[id]}
            onIssueSelection={onIssueSelection}
            organization={repository.owner.login}
            defaultRepositoryNameWithOwner={repository.nameWithOwner}
            pickerType={pickerType}
            anchorElement={itemPickerAnchorElement}
          />
          {dependenciesPickerState && (
            <DependenciesPicker
              type={dependenciesPickerState}
              onClose={onDependenciesPickerClose}
              issueId={id}
              organization={repository.owner.login}
              defaultRepositoryNameWithOwner={repository.nameWithOwner}
              anchorRef={sectionHeaderRef}
              onError={setDependencyAlertsFromErrors}
              onSelection={onSelection}
            />
          )}
          {alertPickerOpen && (
            <CodeScanningAlertPicker
              anchorElement={itemPickerAnchorElement}
              selectedAlerts={initialAlertSelection}
              onSelection={onSelection}
              onError={setAlertTrackingErrors}
              initialOwner={repository.owner.login}
              initialRepo={repository.nameWithOwner.slice(repository.owner.login.length + 1)}
              issueId={databaseId || 0}
            />
          )}
          {dependencyAlerts.length > 0 && (
            <RelationshipsAlertDialog
              title={`Failed to save ${dependencyAlerts.length} ${dependencyAlerts.length > 1 ? 'issues' : 'issue'}`}
              onClose={resetDependencyAlerts}
              width="large"
              data-testid="dependency-alert-dialog"
            >
              {dependencyAlerts.map((dependencyAlert, i) => (
                <Banner
                  // Index is unfortunately the only unique property we have of this alert
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={i}
                  variant="critical"
                  className={styles.DependencyErrorBanner}
                  aria-label={dependencyAlert.title}
                >
                  <Banner.Title>{dependencyAlert.title}</Banner.Title>
                  <Banner.Description>{dependencyAlert.body}</Banner.Description>
                </Banner>
              ))}
            </RelationshipsAlertDialog>
          )}
          {alertTrackingErrors.length > 0 && (
            <RelationshipsAlertDialog
              title={`Failed to save ${alertTrackingErrors.length} ${alertTrackingErrors.length > 1 ? 'alerts' : 'alert'}`}
              onClose={resetAlertTrackingErrors}
              width="large"
              data-testid="alert-tracking-error-dialog"
            >
              {alertTrackingErrors.map((alertTrackingError, i) => (
                <Banner
                  // Index is unfortunately the only unique property we have of this alert
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={i}
                  variant="critical"
                  className={styles.AlertTrackingErrorBanner}
                  aria-label={alertTrackingError.title}
                >
                  <Banner.Title>{alertTrackingError.title}</Banner.Title>
                  <Banner.Description>{alertTrackingError.body}</Banner.Description>
                </Banner>
              ))}
            </RelationshipsAlertDialog>
          )}
          <ActionMenu open={menuOpen} onOpenChange={open => setMenuOpen(open)} anchorRef={sectionHeaderRef}>
            <ActionMenu.Overlay width="medium">
              <ActionList>
                <ActionList.Group>
                  <CommandActionListItem commandId="issue-viewer:edit-parent">
                    {parent ? 'Change or remove parent' : 'Add parent'}
                  </CommandActionListItem>
                  <CommandActionListItem commandId="issue-viewer:mark-blocked-by">
                    {hasBlockedBy ? 'Add or change blocked by' : 'Mark as blocked by'}
                  </CommandActionListItem>
                  <CommandActionListItem commandId="issue-viewer:mark-blocking">
                    {hasBlocking ? 'Add or change blocking' : 'Mark as blocking'}
                  </CommandActionListItem>
                  {isFeatureEnabled('code_scanning_alert_tracking_links_phase_2') && (
                    <CommandActionListItem commandId="issue-viewer:edit-security-alert">
                      {hasSecurityAlerts ? 'Change or add security alerts' : 'Add security alert'}
                    </CommandActionListItem>
                  )}
                </ActionList.Group>
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </>
      }
    >
      {alert && (
        <RelationshipsAlertDialog title={alert.title} onClose={resetAlert}>
          {alert.body}
        </RelationshipsAlertDialog>
      )}
      <ActionList variant="full" className={styles.ActionList_Relationship_Overrides}>
        {parent && (
          <ActionList.Group>
            <ActionList.GroupHeading as="h4" className={styles.ActionList_GroupHeading}>
              {LABELS.relationNames.parentIssue}
            </ActionList.GroupHeading>
            <ParentIssue onLinkClick={onLinkClick} issueKey={parent} />
          </ActionList.Group>
        )}
        {(hasBlockedBy || hasInaccessibleBlockedByIssues) && (
          <>
            {parent && <ActionList.Divider className={styles.ActionList_Divider} />}
            <ActionList.Group>
              <ActionList.GroupHeading as="h4" className={styles.ActionList_GroupHeading}>
                {LABELS.relationNames.blockedByIssues}
                {issueDependenciesSummary.blockedBy > 0 && (
                  <CounterLabel className={styles.RelationshipCountLabel} scheme="secondary">
                    {issueDependenciesSummary.blockedBy}
                  </CounterLabel>
                )}
              </ActionList.GroupHeading>
              {hasBlockedBy &&
                topBlockedBy.nodes
                  .filter(i => !!i)
                  .map(blockedIssue => (
                    <DependencyIssue key={blockedIssue.id} onLinkClick={onLinkClick} issueKey={blockedIssue} />
                  ))}
              {hasInaccessibleBlockedByIssues && (
                <ActionList.Item disabled aria-disabled="true">
                  <ActionList.LeadingVisual>
                    <LockIcon size={16} />
                  </ActionList.LeadingVisual>
                  <span className="text-small fgColor-muted" data-testid="private-blocked-by-notice">
                    Private issues are hidden.
                  </span>
                </ActionList.Item>
              )}
              {hasViewAllBlockedBy && (
                <RelationshipsViewAllButton
                  dialogTitle={LABELS.relationNames.blockedByIssues}
                  countOpenItems={issueDependenciesSummary.blockedBy}
                >
                  <LazyRelationshipsBlockedByListView itemId={id} blockedByCount={issueDependenciesSummary.blockedBy} />
                </RelationshipsViewAllButton>
              )}
            </ActionList.Group>
          </>
        )}
        {(hasBlocking || hasInaccessibleBlockingIssues) && (
          <>
            {(parent || hasBlockedBy || hasInaccessibleBlockedByIssues) && (
              <ActionList.Divider className={styles.ActionList_Divider} />
            )}
            <ActionList.Group>
              <ActionList.GroupHeading as="h4" className={styles.ActionList_GroupHeading}>
                {LABELS.relationNames.blockingIssues}
                {issueDependenciesSummary.blocking > 0 && (
                  <CounterLabel className={styles.RelationshipCountLabel} scheme="secondary">
                    {issueDependenciesSummary.blocking}
                  </CounterLabel>
                )}
              </ActionList.GroupHeading>
              {hasBlocking &&
                topBlocking.nodes
                  .filter(i => !!i)
                  .map(blockingIssue => (
                    <DependencyIssue key={blockingIssue.id} onLinkClick={onLinkClick} issueKey={blockingIssue} />
                  ))}
              {hasInaccessibleBlockingIssues && (
                <ActionList.Item disabled aria-disabled="true">
                  <ActionList.LeadingVisual>
                    <LockIcon size={16} />
                  </ActionList.LeadingVisual>
                  <span className="text-small fgColor-muted" data-testid="private-blocking-notice">
                    Private issues are hidden.
                  </span>
                </ActionList.Item>
              )}
              {hasViewAllBlocking && (
                <RelationshipsViewAllButton
                  dialogTitle={LABELS.relationNames.blockingIssues}
                  countOpenItems={issueDependenciesSummary.blocking}
                >
                  <LazyRelationshipsBlockingListView itemId={id} blockingCount={issueDependenciesSummary.blocking} />
                </RelationshipsViewAllButton>
              )}
            </ActionList.Group>
          </>
        )}
        {hasSecurityAlerts && (
          <>
            {showDividerForSecurityAlerts && <ActionList.Divider className={styles.ActionList_Divider} />}
            <ActionList.Group>
              <ActionList.GroupHeading as="h4" className={styles.ActionList_GroupHeading}>
                {LABELS.relationNames.securityAlerts}
                {topSecurityAlerts && topSecurityAlerts.totalCount > 0 && (
                  <CounterLabel className={styles.RelationshipCountLabel} scheme="secondary">
                    {topSecurityAlerts.totalCount}
                  </CounterLabel>
                )}
              </ActionList.GroupHeading>
              {hasSecurityAlerts &&
                topSecurityAlerts.nodes
                  .filter(a => !!a)
                  .slice(0, VISIBLE_ALERTS_THRESHOLD)
                  .map(a => (
                    <SecurityAlert
                      key={`${a.repository?.nameWithOwner}-${a.number}`}
                      securityAlert={a as SecurityAlert}
                      onLinkClick={onLinkClick}
                    />
                  ))}
              {hasViewAllSecurityAlerts && (
                <RelationshipsViewAllButton
                  dialogTitle={LABELS.relationNames.securityAlerts}
                  countOpenItems={topSecurityAlerts?.totalCount || 0}
                >
                  <LazyRelationshipsSecurityAlertsListView issueId={id} />
                </RelationshipsViewAllButton>
              )}
            </ActionList.Group>
          </>
        )}
      </ActionList>
    </Section>
  )
}
